import { createContext, useContext, useState, useRef, useEffect, useMemo, type ReactNode } from 'react'

interface PomodoroSession {
  id: string
  label: string | null
  workMinutes: number
  breakMinutes: number
  completedCycles: number
  startedAt: string
  endedAt: string | null
}

type Phase = 'work' | 'break' | 'idle'

interface PomodoroContextType {
  phase: Phase
  secondsLeft: number
  workMin: number
  breakMin: number
  label: string
  cycles: number
  activeSession: PomodoroSession | null
  isPaused: boolean
  setWorkMin: (min: number) => void
  setBreakMin: (min: number) => void
  setLabel: (label: string) => void
  startSession: (userId: string) => Promise<void>
  stopSession: (userId: string) => Promise<void>
  pause: () => void
  resume: () => void
  isMinimized: boolean
  setIsMinimized: (minimized: boolean) => void
}

const PomodoroContext = createContext<PomodoroContextType | undefined>(undefined)

export function PomodoroProvider({ children }: { readonly children: ReactNode }) {
  const [workMin, setWorkMin] = useState(25)
  const [breakMin, setBreakMin] = useState(5)
  const [label, setLabel] = useState('')
  const [phase, setPhase] = useState<Phase>('idle')
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [activeSession, setActiveSession] = useState<PomodoroSession | null>(null)
  const [cycles, setCycles] = useState(0)
  const [isMinimized, setIsMinimized] = useState(true)
  const [isPaused, setIsPaused] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isPausedRef = useRef(false)

  function startTimer(phase: Phase, minutes: number) {
    setPhase(phase)
    setSecondsLeft(minutes * 60)
    setIsPaused(false)
    isPausedRef.current = false
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => {
      // Skip countdown if paused
      if (isPausedRef.current) return
      
      setSecondsLeft(s => {
        if (s <= 1) {
          clearInterval(intervalRef.current!)
          return 0
        }
        return s - 1
      })
    }, 1000)
  }

  function pause() {
    setIsPaused(true)
    isPausedRef.current = true
  }

  function resume() {
    setIsPaused(false)
    isPausedRef.current = false
  }

  async function startSession(userId: string) {
    const { http } = await import('../api/client')
    const { Endpoints } = await import('../api/endpoints')
    
    const sessionId = await http.post<string>(`${Endpoints.pomodoro}?userId=${userId}`, {
      label: label || null,
      workMinutes: workMin,
      breakMinutes: breakMin,
    })
    
    const allData = await http.get<{ items: PomodoroSession[] }>(Endpoints.pomodoro, { userId })
    const created = (allData.items ?? []).find(s => s.id === sessionId)
    if (created) setActiveSession(created)
    setCycles(0)
    setIsMinimized(false)
    startTimer('work', workMin)
  }

  function handleTimerEnd() {
    if (phase === 'work') {
      const newCycles = cycles + 1
      setCycles(newCycles)
      startTimer('break', breakMin)
      
      // Notification
      if ('Notification' in globalThis && Notification.permission === 'granted') {
        new Notification('Work session complete! 🍅', {
          body: `Take a ${breakMin} minute break.`,
          icon: '/favicon.ico',
        })
      }
    } else if (phase === 'break') {
      startTimer('work', workMin)
      
      // Notification
      if ('Notification' in globalThis && Notification.permission === 'granted') {
        new Notification('Break complete!', {
          body: `Starting next work session (${workMin} min).`,
          icon: '/favicon.ico',
        })
      }
    }
  }

  useEffect(() => {
    if (secondsLeft === 0 && phase !== 'idle') {
      handleTimerEnd()
    }
  }, [secondsLeft])

  async function stopSession(userId: string) {
    if (!activeSession) return
    if (intervalRef.current) clearInterval(intervalRef.current)
    
    const { http } = await import('../api/client')
    const { Endpoints } = await import('../api/endpoints')
    
    await http.put(`${Endpoints.pomodoro}/${activeSession.id}/complete`, { completedCycles: cycles })
    setPhase('idle')
    setActiveSession(null)
    setCycles(0)
    setIsMinimized(true)
    setIsPaused(false)
    isPausedRef.current = false
  }

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in globalThis && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  const value = useMemo<PomodoroContextType>(() => ({
    phase,
    secondsLeft,
    workMin,
    breakMin,
    label,
    cycles,
    activeSession,
    isPaused,
    setWorkMin,
    setBreakMin,
    setLabel,
    startSession,
    stopSession,
    pause,
    resume,
    isMinimized,
    setIsMinimized,
  }), [phase, secondsLeft, workMin, breakMin, label, cycles, activeSession, isPaused, isMinimized])

  return (
    <PomodoroContext.Provider value={value}>
      {children}
    </PomodoroContext.Provider>
  )
}

export function usePomodoroContext() {
  const context = useContext(PomodoroContext)
  if (context === undefined) {
    throw new Error('usePomodoroContext must be used within a PomodoroProvider')
  }
  return context
}
